function record(value, path) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new TypeError(`${path} must be an object`);
    }
    return value;
}

function requiredString(value, path) {
    if (typeof value !== "string" || !value.trim() || value !== value.trim()) {
        throw new TypeError(`${path} must be a non-empty trimmed string`);
    }
    return value;
}

function optionalString(value, path) {
    if (value === null || value === undefined || value === "") {
        return "";
    }
    return requiredString(value, path);
}

function escapeHtml(value) {
    return String(value ?? "").replace(
        /[&<>"']/g,
        (character) => ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            "\"": "&quot;",
            "'": "&#39;",
        })[character],
    );
}

function normalizeOption(value, path) {
    const option = record(value, path);
    const fields = Object.keys(option).sort();
    if (fields.join(",") !== "id,label,title") {
        throw new TypeError(`${path} fields must be exactly: id, label, title`);
    }
    return Object.freeze({
        id: requiredString(option.id, `${path}.id`),
        label: requiredString(option.label, `${path}.label`),
        title: optionalString(option.title, `${path}.title`),
    });
}

function normalizeState(value) {
    const state = record(value, "loot condition switcher");
    const fields = Object.keys(state).sort();
    if (
        fields.join(",")
        !== "disabled,nextLabel,options,previousLabel,selectedIndex,selectionKey,unselectedLabel"
    ) {
        throw new TypeError(
            "loot condition switcher fields must be exactly: disabled, nextLabel, options, previousLabel, selectedIndex, selectionKey, unselectedLabel",
        );
    }
    if (!Array.isArray(state.options) || state.options.length === 0) {
        throw new TypeError("loot condition switcher options must be a non-empty array");
    }
    const options = Object.freeze(state.options.map((option, index) => (
        normalizeOption(option, `loot condition switcher.options[${index}]`)
    )));
    const selectedIndex = state.selectedIndex === null
        ? null
        : Number(state.selectedIndex);
    if (selectedIndex !== null && (
        !Number.isInteger(selectedIndex)
        || selectedIndex < 0
        || selectedIndex >= options.length
    )) {
        throw new TypeError("loot condition switcher selectedIndex is out of range");
    }
    if (typeof state.disabled !== "boolean") {
        throw new TypeError("loot condition switcher disabled must be a boolean");
    }
    return Object.freeze({
        selectionKey: requiredString(
            state.selectionKey,
            "loot condition switcher.selectionKey",
        ),
        selectedIndex,
        options,
        previousLabel: requiredString(
            state.previousLabel,
            "loot condition switcher.previousLabel",
        ),
        nextLabel: requiredString(
            state.nextLabel,
            "loot condition switcher.nextLabel",
        ),
        unselectedLabel: requiredString(
            state.unselectedLabel,
            "loot condition switcher.unselectedLabel",
        ),
        disabled: state.disabled,
    });
}

export function nextLootConditionOptionIndex(currentIndex, direction, optionCount) {
    if (
        !Number.isInteger(currentIndex)
        || !Number.isInteger(optionCount)
        || optionCount < 1
        || currentIndex < 0
        || currentIndex >= optionCount
    ) {
        throw new TypeError("loot condition switcher index is out of range");
    }
    if (direction !== -1 && direction !== 1) {
        throw new TypeError("loot condition switcher direction must be -1 or 1");
    }
    return (currentIndex + direction + optionCount) % optionCount;
}

function optionButtonMarkup(state, direction, label) {
    const optionIndex = state.selectedIndex === null
        ? direction < 0 ? state.options.length - 1 : 0
        : nextLootConditionOptionIndex(
            state.selectedIndex,
            direction,
            state.options.length,
        );
    const option = state.options[optionIndex];
    const disabled = state.disabled ? " disabled" : "";
    return `<button class="btn btn-ghost btn-xs fishy-loot-condition-button" type="button" data-loot-condition-direction="${direction}" data-loot-condition-key="${escapeHtml(state.selectionKey)}" data-loot-condition-option-index="${optionIndex}" data-loot-condition-option-id="${escapeHtml(option.id)}" aria-label="${escapeHtml(label)}" data-fishy-tooltip="${escapeHtml(label)}"${disabled}>${direction < 0 ? "&lt;" : "&gt;"}</button>`;
}

function initialOptionSelectionMarkup(state) {
    const option = state.options[0];
    const disabled = state.disabled ? " disabled" : "";
    const accessibleLabel = option.title || option.label;
    return `<button class="btn btn-ghost btn-xs fishy-loot-group-condition" type="button" data-loot-condition-direction="1" data-loot-condition-key="${escapeHtml(state.selectionKey)}" data-loot-condition-option-index="0" data-loot-condition-option-id="${escapeHtml(option.id)}" aria-label="${escapeHtml(accessibleLabel)}" data-fishy-tooltip="${escapeHtml(accessibleLabel)}"${disabled}>${escapeHtml(option.label)}</button>`;
}

/**
 * Owns the condition label and previous/next control markup shared by map loot
 * groups and the calculator Data editor. Selection semantics remain with each
 * caller; this renderer only exposes the stable target option on each button.
 */
export function lootConditionSwitcherMarkup(value) {
    const state = normalizeState(value);
    const selected = state.selectedIndex === null
        ? null
        : state.options[state.selectedIndex];
    const title = selected?.title || selected?.label || state.unselectedLabel;
    const label = `<div class="fishy-loot-group-condition" tabindex="0" aria-label="${escapeHtml(title)}" data-fishy-tooltip="${escapeHtml(title)}" data-loot-condition-label${selected ? "" : " data-loot-condition-unselected"}>${escapeHtml(selected?.label ?? state.unselectedLabel)}</div>`;
    if (state.selectedIndex === null) {
        return `<div class="fishy-loot-condition-control" data-loot-condition-switcher data-loot-condition-key="${escapeHtml(state.selectionKey)}">`
            + label
            + initialOptionSelectionMarkup(state)
            + "</div>";
    }
    if (state.options.length === 1) {
        return label;
    }
    return `<div class="fishy-loot-condition-control" data-loot-condition-switcher data-loot-condition-key="${escapeHtml(state.selectionKey)}">`
        + optionButtonMarkup(state, -1, state.previousLabel)
        + label
        + optionButtonMarkup(state, 1, state.nextLabel)
        + "</div>";
}

export function lootConditionSwitcherActionFromTrigger(trigger) {
    const key = String(trigger?.getAttribute?.("data-loot-condition-key") ?? "").trim();
    const optionId = String(
        trigger?.getAttribute?.("data-loot-condition-option-id") ?? "",
    ).trim();
    const optionIndex = Number.parseInt(
        trigger?.getAttribute?.("data-loot-condition-option-index"),
        10,
    );
    if (!key || !optionId || !Number.isInteger(optionIndex) || optionIndex < 0) {
        throw new TypeError("loot condition switcher trigger is incomplete");
    }
    return Object.freeze({ key, optionId, optionIndex });
}
